import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Wrench, Plus, Check, X } from 'lucide-react';

// Global-Administrator-managed ROUT trade catalog: add, rename, reorder, activate/deactivate.
// No hard delete (trades may be referenced by requests/WOs/proposals). Separate from client
// source categories.
const TradeManager = () => {
  const { token, user } = useContext(AuthContext);
  const isOwner = user?.role === 'owner';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [trades, setTrades] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/trades?include_inactive=true`, authHeaders);
    setTrades(r.data || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/trades`, { name: newName.trim() }, authHeaders);
      setNewName(''); toast.success('Trade added'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add trade'); }
  };

  const saveRename = async (id) => {
    try {
      await axios.put(`${API}/trades/${id}`, { name: editName.trim() }, authHeaders);
      setEditId(null); toast.success('Trade renamed'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API}/trades/${t.id}`, { is_active: !t.is_active }, authHeaders);
      toast.success(t.is_active ? 'Trade deactivated' : 'Trade reactivated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const move = async (idx, dir) => {
    const next = [...trades];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setTrades(next);
    try {
      await axios.post(`${API}/trades/reorder`, { ordered_ids: next.map((t) => t.id) }, authHeaders);
    } catch (e) { toast.error('Could not reorder'); load(); }
  };

  return (
    <div data-testid="trade-manager">
      <p className="text-sm text-slate-500 mb-4">ROUT service trades used across requests, work orders and pricing. Kept separate from client source categories (e.g., a client's "Building & Grounds" category maps to the ROUT trade "Janitorial / Cleaning").</p>
      {isOwner && (
        <div className="flex items-center gap-2 mb-4 max-w-md">
          <Input placeholder="New trade name…" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            data-testid="trade-new-input" />
          <button onClick={add} data-testid="trade-add-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] whitespace-nowrap">
            <Plus size={15} /> Add
          </button>
        </div>
      )}
      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {trades.map((t, idx) => (
            <div key={t.id} data-testid={`trade-row-${t.id}`} className="flex items-center gap-3 px-4 py-3">
              <span className="text-xs font-mono text-slate-300 w-6 text-right">{idx + 1}</span>
              <Wrench size={16} className="text-[#8FC93A] shrink-0" />
              {editId === t.id ? (
                <>
                  <Input className="max-w-xs" value={editName}
                    onChange={(e) => setEditName(e.target.value)} data-testid="trade-edit-input" />
                  <button onClick={() => saveRename(t.id)} className="text-green-600" data-testid="trade-save-btn"><Check size={18} /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400"><X size={18} /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm font-medium ${t.is_active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{t.name}</span>
                  {!t.is_active && <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">Inactive</span>}
                  {isOwner && (
                    <>
                      <button onClick={() => move(idx, -1)} className="text-xs text-slate-400 hover:text-[#0E2A47]" title="Move up">▲</button>
                      <button onClick={() => move(idx, 1)} className="text-xs text-slate-400 hover:text-[#0E2A47]" title="Move down">▼</button>
                      <button onClick={() => { setEditId(t.id); setEditName(t.name); }}
                        className="text-xs font-semibold text-[#0E2A47] hover:underline" data-testid={`trade-rename-${t.id}`}>Rename</button>
                      <Switch checked={t.is_active} onCheckedChange={() => toggleActive(t)} data-testid={`trade-toggle-${t.id}`} />
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {trades.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">No trades defined.</div>}
        </CardContent>
      </Card>
      {!isOwner && <p className="mt-3 text-xs text-slate-400">Only a Global Administrator can add, rename, reorder or deactivate trades.</p>}
    </div>
  );
};

export default TradeManager;
